import { useEffect, useState } from "react";
import { Button } from "@humansignal/ui";
import { cn } from "@afriannotate/datamanager/utils/bem";
import { confirm } from "@afriannotate/datamanager/components/Common/Modal/Modal";

const block = cn("collection-submissions");

const STATE_LABELS = { accepted: "Accepted", rejected: "Rejected", fixed: "Fixed and accepted" };

const RULE_TEXT = {
  until_reviewed: "You can edit or delete a record until a reviewer has looked at it.",
  "24h": "You can edit or delete a record for 24 hours after saving it.",
  never: "Saved records cannot be changed.",
};

const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(Date.now() - 864e5);
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === today.toDateString()) return `Today ${time}`;
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;
  return `${d.toLocaleDateString()} ${time}`;
};

/** The contributor's own records on the current prompt, newest first, with
 *  Edit and Delete while the project's edit rule allows. */
export const CollectionSubmissions = ({ lsf, onClose }) => {
  const [data, setData] = useState(lsf?.repeatState ?? null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    lsf?.refreshRepeat(lsf?.task?.id).then((v) => {
      if (!alive) return;
      if (v) setData(v);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [lsf]);

  const records = data?.records ?? [];

  const remove = (row) =>
    confirm({
      title: "Delete this record?",
      body: "The record and its answers are removed from the project. This cannot be undone.",
      okText: "Delete",
      buttonLook: "negative",
      onOk: async () => {
        setBusy(row.id);
        const ok = await lsf.deleteOwn(row.id);
        setBusy(null);
        if (ok) setData((d) => ({ ...d, records: d.records.filter((r) => r.id !== row.id), mine: (d.mine ?? 1) - 1 }));
      },
    });

  return (
    <div className={block.toClassName()}>
      <p className={block.elem("rule").toClassName()}>
        {data?.goal ? `${data.mine ?? records.length} of ${data.goal} records. ` : ""}
        {RULE_TEXT[data?.edit_rule] ?? ""}
      </p>
      {loading && !records.length ? <p className={block.elem("empty").toClassName()}>Loading…</p> : null}
      {!loading && !records.length ? (
        <p className={block.elem("empty").toClassName()}>
          No records saved on this prompt yet. Records waiting to upload from this device appear once they are sent.
        </p>
      ) : null}
      <ul className={block.elem("list").toClassName()}>
        {records.map((row) => (
          <li key={row.id} className={block.elem("row").toClassName()}>
            {row.thumbnail ? (
              <img className={block.elem("thumb").toClassName()} src={row.thumbnail} alt="" loading="lazy" />
            ) : (
              <span className={block.elem("thumb").mod({ empty: true }).toClassName()} aria-hidden="true" />
            )}
            <div className={block.elem("text").toClassName()}>
              <span className={block.elem("title").toClassName()}>{row.text}</span>
              <span className={block.elem("meta").toClassName()}>
                {when(row.created_at)}
                {row.media?.length ? ` (${row.media.join(", ")})` : ""}
                {row.review_state ? ` ${STATE_LABELS[row.review_state] ?? "Reviewed"}` : ""}
              </span>
            </div>
            {row.can_change ? (
              <span className={block.elem("actions").toClassName()}>
                <Button
                  look="outlined"
                  size="small"
                  disabled={busy === row.id}
                  onClick={() => {
                    onClose?.();
                    lsf.editOwn(row.id);
                  }}
                >
                  Edit
                </Button>
                <Button
                  look="outlined"
                  variant="negative"
                  size="small"
                  disabled={busy === row.id}
                  onClick={() => remove(row)}
                >
                  Delete
                </Button>
              </span>
            ) : (
              <span className={block.elem("locked").toClassName()} title={row.locked_reason || ""}>
                {row.review_state ? "Reviewed" : "Saved"}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
